import { apiClient } from '@/shared/services/api';
import {
  getRefreshToken,
  extractTokensFromResponse,
  setTokenExpiration,
  setAccessTokenToLocalStorage,
  setRefreshTokenToLocalStorage,
  setAccessTokenToSessionStorage,
  setRefreshTokenToSessionStorage,
  clearCredentials,
  getActiveStorageType
} from '@/shared/utils/tokenHelper';
import { getTranslatedErrorMessage } from '@/shared/utils/errorMessages';
import { retryWithBackoff } from '@/shared/utils/retry';
import i18n from '@/i18n';
const { t } = i18n.global;

// Helper function to extract rate limit info from error response
const extractRateLimitInfo = (error) => {
  let retryAfter = 0;
  let lockDuration = 0;

  if (error?.response) {
    // Check Retry-After header (can be in seconds or as a date string)
    const retryAfterHeader = error.response.headers?.['retry-after'] || error.response.headers?.['Retry-After'];
    if (retryAfterHeader) {
      // If it's a number, use it directly
      const parsed = Number.parseInt(retryAfterHeader, 10);
      if (Number.isFinite(parsed)) {
        retryAfter = parsed;
      } else {
        // If it's a date string, calculate seconds until that date
        const retryDate = new Date(retryAfterHeader);
        if (Number.isFinite(retryDate.getTime())) {
          retryAfter = Math.max(0, Math.ceil((retryDate.getTime() - Date.now()) / 1000));
        }
      }
    }

    // Check response body for retryAfter or lockDuration
    const responseData = error.response.data || {};
    if (responseData.retryAfter) {
      retryAfter = Number.parseInt(responseData.retryAfter, 10) || retryAfter;
    }
    if (responseData.lockDuration) {
      lockDuration = Number.parseInt(responseData.lockDuration, 10) || 0;
    }
  }

  return { retryAfter, lockDuration };
};

export const signup = async (formData) => {

  const returnObject = { success: false, message: '', data: null, requiresVerification: false, retryAfter: 0 };

  await retryWithBackoff(
    () => apiClient.post('/auth/sign-up', formData)
  ).then((response) => {

    returnObject.data = response.data;
    // Also include code directly in returnObject if present
    if (response.data?.code) {
      returnObject.code = response.data.code;
    }

    // Check if email verification is required
    const responseCode = response.data?.code || response.data?.message;
    const responseStatus = response.data?.status || response.status;
    if (responseCode === 'AUTH_EMAIL_VERIFICATION_REQUIRED' || responseStatus === 201) {
      returnObject.success = true; // Still success, but requires verification
      returnObject.requiresVerification = true;
      returnObject.message = response.data?.message || t('auth.emailVerificationRequired') || 'Please verify your email to continue';
      // Extract OTP request token and available methods for two-step flow
      if (response.data?.data) {
        returnObject.otpRequestToken = response.data.data.otpRequestToken;
        returnObject.availableMethods = response.data.data.availableMethods;
        returnObject.email = response.data.data.email;
        returnObject.phoneNumber = response.data.data.phoneNumber;
        returnObject.tokenExpiresAt = response.data.data.tokenExpiresAt;
      }
    } else if (responseCode === 'AUTH_SIGNUP_SUCCESS' || responseStatus === 200) {
      returnObject.success = true;
      returnObject.requiresVerification = false;
      returnObject.message = t('auth.signupSuccess') || response.data?.message || 'Account created successfully';
      // Extract and store token expiration if tokens are present
      const tokens = extractTokensFromResponse(response);
      if (tokens.accessToken && tokens.expiresIn && typeof tokens.expiresIn === 'number' && tokens.expiresIn > 0) {
        setTokenExpiration(tokens.expiresIn);
      }
    } else {
      // Default: treat as success if status is 200
      returnObject.success = true;
      returnObject.requiresVerification = true; // Default to requiring verification for safety
      // Extract and store token expiration if tokens are present
      const tokens = extractTokensFromResponse(response);
      if (tokens.accessToken && tokens.expiresIn && typeof tokens.expiresIn === 'number' && tokens.expiresIn > 0) {
        setTokenExpiration(tokens.expiresIn);
      }
    }

  }).catch((error) => {

    // Extract rate limit info
    const rateLimitInfo = extractRateLimitInfo(error);
    returnObject.retryAfter = rateLimitInfo.retryAfter;

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.SIGNUP_FAILED');
    }
  })

  return returnObject;
};


export const signin = async (formData) => {

  const returnObject = {
    success: true,
    message: '',
    data: null,
    requiresVerification: false,
    retryAfter: 0,
    accountLocked: false,
    ipBlocked: false,
    lockDuration: 0,
    // Deactivated/deleted account handling
    code: null,
    accountDeactivated: false,
    accountDeleted: false,
    canReactivate: false,
    deactivatedAt: null,
  };

  // Include rememberMe and recaptchaToken in the backend request
  const signInData = {
    email: formData.email,
    password: formData.password,
    rememberMe: formData.rememberMe ?? false, // Send rememberMe to backend
  };

  // Only include recaptchaToken if it's actually provided (not null/undefined)
  if (formData.recaptchaToken) {
    signInData.recaptchaToken = formData.recaptchaToken;
  }

  await retryWithBackoff(
    () => apiClient.post('/auth/sign-in', signInData)
  ).then((response) => {

    returnObject.data = response.data;

    // Check if email verification is required or email was resent
    const responseCode = response.data?.code || response.data?.message;
    if (responseCode === 'AUTH_EMAIL_VERIFICATION_REQUIRED' || responseCode === 'AUTH_VERIFICATION_EMAIL_RESENT' || responseCode === 'AUTH_VERIFICATION_REQUIRED') {
      returnObject.success = true; // Still success, but requires verification
      returnObject.requiresVerification = true;
      if (responseCode === 'AUTH_VERIFICATION_EMAIL_RESENT') {
        returnObject.message = t('auth.verificationEmailResent') || 'A verification email has been sent. Please check your email.';
      } else {
        returnObject.message = t('auth.emailVerificationRequired') || 'Please verify your email to continue';
      }
      // Extract OTP request token and available methods for two-step flow
      if (response.data?.data) {
        returnObject.otpRequestToken = response.data.data.otpRequestToken;
        returnObject.availableMethods = response.data.data.availableMethods;
        returnObject.email = response.data.data.email;
        returnObject.phoneNumber = response.data.data.phoneNumber;
        returnObject.tokenExpiresAt = response.data.data.tokenExpiresAt;
      }
    } else {
      // Extract and store token expiration if tokens are present
      const tokens = extractTokensFromResponse(response);
      if (tokens.accessToken && tokens.expiresIn && typeof tokens.expiresIn === 'number' && tokens.expiresIn > 0) {
        setTokenExpiration(tokens.expiresIn);
      }
    }

  }).catch((error) => {

    // Extract rate limit info
    const rateLimitInfo = extractRateLimitInfo(error);
    returnObject.retryAfter = rateLimitInfo.retryAfter;
    returnObject.lockDuration = rateLimitInfo.lockDuration;

    const status = error?.response?.status;
    // Check if error is due to email verification requirement or email was resent
    const errorCode = error?.response?.data?.code || error?.response?.data?.message;
    if (errorCode === 'AUTH_EMAIL_VERIFICATION_REQUIRED' || errorCode === 'AUTH_VERIFICATION_EMAIL_RESENT' || errorCode === 'AUTH_VERIFICATION_REQUIRED') {
      returnObject.success = true; // Still success, but requires verification
      returnObject.requiresVerification = true;
      if (errorCode === 'AUTH_VERIFICATION_EMAIL_RESENT') {
        returnObject.message = t('auth.verificationEmailResent') || 'A verification email has been sent. Please check your email.';
      } else {
        returnObject.message = t('auth.emailVerificationRequired') || 'Please verify your email to continue';
      }
      // Extract OTP request token and available methods for two-step flow
      if (error?.response?.data?.data) {
        returnObject.otpRequestToken = error.response.data.data.otpRequestToken;
        returnObject.availableMethods = error.response.data.data.availableMethods;
        returnObject.email = error.response.data.data.email;
        returnObject.phoneNumber = error.response.data.data.phoneNumber;
        returnObject.tokenExpiresAt = error.response.data.data.tokenExpiresAt;
      }
    } else if (status === 503) {
      returnObject.success = false;
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else if (errorCode === 'IP_BLOCKED' || errorCode === 'AUTH_IP_BLOCKED') {
      returnObject.success = false;
      returnObject.ipBlocked = true;
      // Use lockDuration if provided, otherwise use retryAfter
      if (rateLimitInfo.lockDuration > 0) {
        returnObject.retryAfter = rateLimitInfo.lockDuration;
      } else if (rateLimitInfo.retryAfter > 0) {
        returnObject.retryAfter = rateLimitInfo.retryAfter;
      }
      // Provide user-friendly message with retry time
      const retryTime = returnObject.retryAfter || 0;
      const minutes = Math.floor(retryTime / 60);
      const seconds = retryTime % 60;
      let timeText = '';
      if (minutes > 0) {
        timeText = minutes === 1 ? t('auth.minute', { count: 1 }) || '1 minute' : t('auth.minutes', { count: minutes }) || `${minutes} minutes`;
      }
      if (seconds > 0 && minutes === 0) {
        timeText = seconds === 1 ? t('auth.second', { count: 1 }) || '1 second' : t('auth.seconds', { count: seconds }) || `${seconds} seconds`;
      }
      returnObject.message = t('auth.ipBlockedMessage', { time: timeText }) ||
        `Too many login attempts from this IP address. Please try again in ${timeText || retryTime + ' seconds'}.`;
    } else if (errorCode === 'AUTH_ACCOUNT_LOCKED') {
      returnObject.success = false;
      returnObject.accountLocked = true;
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.SIGNIN_FAILED');
      // Use lockDuration if provided, otherwise use retryAfter
      if (rateLimitInfo.lockDuration > 0) {
        returnObject.retryAfter = rateLimitInfo.lockDuration;
      }
    } else if (errorCode === 'AUTH_ACCOUNT_DEACTIVATED') {
      returnObject.success = false;
      returnObject.code = 'AUTH_ACCOUNT_DEACTIVATED';
      returnObject.accountDeactivated = true;

      // Backend envelope: { ..., data: { canReactivate, deactivatedAt, userId } }
      const extra = error?.response?.data?.data;
      returnObject.canReactivate = extra?.canReactivate === true;
      returnObject.deactivatedAt = extra?.deactivatedAt ?? null;
      returnObject.message =
        error?.response?.data?.message ||
        t('auth.errorCodification.AUTH_ACCOUNT_DEACTIVATED');
    } else if (errorCode === 'AUTH_ACCOUNT_DELETED') {
      returnObject.success = false;
      returnObject.code = 'AUTH_ACCOUNT_DELETED';
      returnObject.accountDeleted = true;
      returnObject.message = error?.response?.data?.message || t('auth.serviceErrors.SIGNIN_FAILED');
    } else {
      returnObject.success = false;
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.SIGNIN_FAILED');
    }

  })

  return returnObject;
}

export const restPassword = async (formData) => {

  const returnObject = { success: true, message: '', data: null, retryAfter: 0 };

  await retryWithBackoff(
    () => apiClient.post('/auth/reset-password', formData)
  ).then((response) => {

    returnObject.success = true;
    // Extract OTP request token and available methods for two-step flow
    if (response.data?.data) {
      returnObject.otpRequestToken = response.data.data.otpRequestToken;
      returnObject.availableMethods = response.data.data.availableMethods;
      returnObject.tokenExpiresAt = response.data.data.tokenExpiresAt;
    }

  }).catch((error) => {
    // Extract rate limit info
    const rateLimitInfo = extractRateLimitInfo(error);
    returnObject.retryAfter = rateLimitInfo.retryAfter;

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.RESET_PASSWORD_FAILED');
    }
  })

  return returnObject;
}

export const resendPasswordRecovery = async (formData) => {

  const returnObject = { success: true, message: '', data: null, retryAfter: 0 };

  await retryWithBackoff(
    () => apiClient.post('/auth/resend-password-reset-code', formData)
  ).then((response) => {

    returnObject.success = true;

  }).catch((error) => {
    returnObject.status = error?.response?.data?.status
    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.RESET_PASSWORD_FAILED');
    }
  })

  return returnObject;
}


export const verifyCode = async (formData) => {

  const returnObject = { success: false, message: '', resetToken: null };

  await retryWithBackoff(
    () => apiClient.post('/auth/verify-code', formData)
  ).then((response) => {

    returnObject.success = true;
    returnObject.resetToken = response.data.data.token;

  }).catch((error) => {
    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.CODE_VERIFICATION_FAILED');
    }
  })

  return returnObject;
}


export const changePassword = async (formData) => {

  const returnObject = { success: false, message: '' };

  await retryWithBackoff(
    () => apiClient.post('/auth/change-password', formData)
  ).then((response) => {

    returnObject.success = true;

  }).catch((error) => {
    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.CHANGE_PASSWORD_FAILED');
    }
  })

  return returnObject;
}

export const logOut = async () => {

  const returnObject = { success: false, message: '' };
  const postData = {
    refreshToken: getRefreshToken() // Checks both localStorage and sessionStorage
  }

  await retryWithBackoff(
    () => apiClient.post('/auth/logout', postData)
  ).then((response) => {

    returnObject.success = true;

  }).catch((error) => {
    returnObject.success = false;
    const status = error?.response?.status;
    // Even if API call fails, consider it successful if we're logging out
    // The backend may have already invalidated the session
    // If token is already invalid/expired (401/403), logout is effectively successful
    if (status === 401 || status === 403) {
      // Token already invalid/expired - logout is effectively successful
      returnObject.success = true;
      returnObject.message = t('auth.logoutSuccess') || 'Logged out successfully';
    } else if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.LOGOUT_FAILED');
    }
  })

  // Clear credentials after API call (regardless of success/failure)
  // This ensures tokens are cleared even if API call fails
  clearCredentials();

  return returnObject;
}


export const changePasswordFromSettings = async (postData) => {

  const returnObject = { success: false, message: '' };

  await retryWithBackoff(
    () => apiClient.post('/auth/change-password-secure', postData)
  ).then((response) => {

    returnObject.success = true;

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.CHANGE_PASSWORD_FAILED');
    }
  })

  return returnObject;
}


export const disactivateAccount = async (postData) => {

  const returnObject = { success: false, message: '' };

  await retryWithBackoff(
    () => apiClient.delete('/users/account')
  ).then((response) => {

    returnObject.success = true;

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.DEACTIVATE_FAILED');
    }
  })

  return returnObject;
}

export const reactivateAccount = async ({ email, password }) => {
  const returnObject = { success: false, message: '', code: null, status: null };

  const payload = {
    email: email?.trim?.() ? email.trim().toLowerCase() : email,
    password,
  };

  await retryWithBackoff(
    () => apiClient.post('/users/public/reactivate', payload)
  )
    .then((response) => {
      returnObject.success = true;
      returnObject.status = response?.status;
      returnObject.message = response?.data?.message || 'Account reactivated successfully';
    })
    .catch((error) => {
      returnObject.success = false;
      returnObject.status = error?.response?.status || null;
      returnObject.code = error?.response?.data?.code || error?.code || null;
      returnObject.message =
        error?.response?.data?.message ||
        (error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : '') ||
        error?.message ||
        'Failed to reactivate account';
    });

  return returnObject;
}

export const getLoginMethods = async () => {
  const returnObject = { success: false, methods: null, message: '' };

  await apiClient.get('/auth/login/methods').then((response) => {
    const payload = response.data;
    if (payload?.success && payload?.data?.methods) {
      returnObject.success = true;
      returnObject.methods = payload.data.methods;
    } else {
      returnObject.message = payload?.message || 'Failed to load login methods';
    }
  }).catch((error) => {
    returnObject.success = false;
    returnObject.message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to load login methods';
  });

  return returnObject;
};

export const getOAuthOnboardingStatus = async () => {
  const returnObject = {
    success: false,
    requiresOnboarding: false,
    missingPassword: false,
    missingTermsAcceptance: false,
    message: '',
  };

  await apiClient.get('/auth/onboarding/status').then((response) => {
    const payload = response.data;
    const data = payload?.data ?? payload;
    returnObject.success = true;
    returnObject.requiresOnboarding = data?.requiresOnboarding === true;
    returnObject.missingPassword = data?.missingPassword === true;
    returnObject.missingTermsAcceptance = data?.missingTermsAcceptance === true;
  }).catch((error) => {
    returnObject.success = false;
    returnObject.message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to load onboarding status';
  });

  return returnObject;
};

export const completeOAuthSetup = async ({ password, acceptTermsAndPrivacy, lang }) => {
  const returnObject = {
    success: false,
    message: '',
    data: null,
    accessToken: null,
    refreshToken: null,
    expiresIn: null,
  };

  await apiClient.post('/auth/oauth/complete-setup', {
    password,
    acceptTermsAndPrivacy,
    ...(lang ? { lang } : {}),
  }).then((response) => {
    const payload = response.data;
    const tokens = extractTokensFromResponse(response);
    if (!tokens.accessToken) {
      returnObject.success = false;
      returnObject.message = t('auth.serviceErrors.OAUTH_EXCHANGE_FAILED');
      return;
    }
    returnObject.success = true;
    returnObject.data = payload?.data ?? payload;
    returnObject.accessToken = tokens.accessToken;
    returnObject.refreshToken = tokens.refreshToken;
    returnObject.expiresIn = tokens.expiresIn;
    returnObject.message = payload?.message || '';
  }).catch((error) => {
    returnObject.success = false;
    const code = error?.response?.data?.code;
    returnObject.message = code
      ? t(`auth.errorCodification.${code}`)
      : (error?.response?.data?.message || error?.message || t('auth.serviceErrors.OAUTH_EXCHANGE_FAILED'));
  });

  return returnObject;
};

export const signupWithGoogle = async (postData, action = 'signup') => {

  const endPoint = action === 'signup' ? 'signupEndpoint' : 'signinWithGoogle';

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.post(endPoint, postData)
  ).then((response) => { // To change

    returnObject.success = true;
    returnObject.data = response.data;

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.GOOGLE_SIGNUP_FAILED');
    }
  })

  return returnObject;
}

export const logOutOfAllDevices = async () => {

  const endPoint = '/auth/logoutFromAllDevices';

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.post(endPoint)
  ).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data;

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.LOGOUT_ALL_DEVICES_FAILED');
    }
  })

  return returnObject;
}



export const signInWithCookies = async (formData) => {

  const returnObject = {
    success: true,
    message: '',
    data: null,
    requiresVerification: false,
    retryAfter: 0,
    accountLocked: false,
    ipBlocked: false,
    lockDuration: 0,
    // Deactivated/deleted account handling
    code: null,
    accountDeactivated: false,
    accountDeleted: false,
    canReactivate: false,
    deactivatedAt: null,
  };

  // Include rememberMe and recaptchaToken in the backend request
  const signInData = {
    email: formData.email,
    password: formData.password,
    rememberMe: formData.rememberMe ?? false, // Send rememberMe to backend
  };

  // Only include recaptchaToken if it's actually provided (not null/undefined)
  if (formData.recaptchaToken) {
    signInData.recaptchaToken = formData.recaptchaToken;
  }

  await retryWithBackoff(
    () => apiClient.post('/auth/cookie/signin', signInData)
  ).then((response) => {

    returnObject.data = response.data;

    // Check if email verification is required or email was resent
    const responseCode = response.data?.code || response.data?.message;
    if (responseCode === 'AUTH_EMAIL_VERIFICATION_REQUIRED' || responseCode === 'AUTH_VERIFICATION_EMAIL_RESENT') {
      returnObject.success = true; // Still success, but requires verification
      returnObject.requiresVerification = true;
      if (responseCode === 'AUTH_VERIFICATION_EMAIL_RESENT') {
        returnObject.message = t('auth.verificationEmailResent') || 'A verification email has been sent. Please check your email.';
      } else {
        returnObject.message = t('auth.emailVerificationRequired') || 'Please verify your email to continue';
      }
    } else {
      // Extract and store token expiration if tokens are present
      const tokens = extractTokensFromResponse(response);
      if (tokens.accessToken && tokens.expiresIn && typeof tokens.expiresIn === 'number' && tokens.expiresIn > 0) {
        setTokenExpiration(tokens.expiresIn);
      }
    }

  }).catch((error) => {

    // Extract rate limit info
    const rateLimitInfo = extractRateLimitInfo(error);
    returnObject.retryAfter = rateLimitInfo.retryAfter;
    returnObject.lockDuration = rateLimitInfo.lockDuration;

    const status = error?.response?.status;
    // Check if error is due to email verification requirement or email was resent
    const errorCode = error?.response?.data?.code || error?.response?.data?.message;
    if (errorCode === 'AUTH_EMAIL_VERIFICATION_REQUIRED' || errorCode === 'AUTH_VERIFICATION_EMAIL_RESENT') {
      returnObject.success = true; // Still success, but requires verification
      returnObject.requiresVerification = true;
      if (errorCode === 'AUTH_VERIFICATION_EMAIL_RESENT') {
        returnObject.message = t('auth.verificationEmailResent') || 'A verification email has been sent. Please check your email.';
      } else {
        returnObject.message = t('auth.emailVerificationRequired') || 'Please verify your email to continue';
      }
    } else if (status === 503) {
      returnObject.success = false;
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else if (errorCode === 'IP_BLOCKED' || errorCode === 'AUTH_IP_BLOCKED') {
      returnObject.success = false;
      returnObject.ipBlocked = true;
      // Use lockDuration if provided, otherwise use retryAfter
      if (rateLimitInfo.lockDuration > 0) {
        returnObject.retryAfter = rateLimitInfo.lockDuration;
      } else if (rateLimitInfo.retryAfter > 0) {
        returnObject.retryAfter = rateLimitInfo.retryAfter;
      }
      // Provide user-friendly message with retry time
      const retryTime = returnObject.retryAfter || 0;
      const minutes = Math.floor(retryTime / 60);
      const seconds = retryTime % 60;
      let timeText = '';
      if (minutes > 0) {
        timeText = minutes === 1 ? t('auth.minute', { count: 1 }) || '1 minute' : t('auth.minutes', { count: minutes }) || `${minutes} minutes`;
      }
      if (seconds > 0 && minutes === 0) {
        timeText = seconds === 1 ? t('auth.second', { count: 1 }) || '1 second' : t('auth.seconds', { count: seconds }) || `${seconds} seconds`;
      }
      returnObject.message = t('auth.ipBlockedMessage', { time: timeText }) ||
        `Too many login attempts from this IP address. Please try again in ${timeText || retryTime + ' seconds'}.`;
    } else if (errorCode === 'AUTH_ACCOUNT_LOCKED') {
      returnObject.success = false;
      returnObject.accountLocked = true;
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.SIGNIN_FAILED');
      // Use lockDuration if provided, otherwise use retryAfter
      if (rateLimitInfo.lockDuration > 0) {
        returnObject.retryAfter = rateLimitInfo.lockDuration;
      }
    } else if (errorCode === 'AUTH_ACCOUNT_DEACTIVATED') {
      returnObject.success = false;
      returnObject.code = 'AUTH_ACCOUNT_DEACTIVATED';
      returnObject.accountDeactivated = true;

      const extra = error?.response?.data?.data;
      returnObject.canReactivate = extra?.canReactivate === true;
      returnObject.deactivatedAt = extra?.deactivatedAt ?? null;
      returnObject.message =
        error?.response?.data?.message ||
        t('auth.errorCodification.AUTH_ACCOUNT_DEACTIVATED');
    } else if (errorCode === 'AUTH_ACCOUNT_DELETED') {
      returnObject.success = false;
      returnObject.code = 'AUTH_ACCOUNT_DELETED';
      returnObject.accountDeleted = true;
      returnObject.message =
        error?.response?.data?.message || t('auth.serviceErrors.SIGNIN_FAILED');
    } else {
      returnObject.success = false;
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.SIGNIN_FAILED');
    }

  })

  return returnObject;
}


export const logOutWithCookies = async () => {

  const returnObject = { success: false, message: '' };
  // No need to send refresh token in body - backend extracts it from cookies
  // Cookies are automatically sent via withCredentials: true

  await retryWithBackoff(
    () => apiClient.post('/auth/cookie/logout')
  ).then((response) => {
    // Check if response is successful (status 200-299)
    if (response.status >= 200 && response.status < 300) {
      returnObject.success = true;
      returnObject.message = response.data?.message || t('auth.logoutSuccess') || 'Logged out successfully';
    } else {
      returnObject.success = false;
      returnObject.message = response.data?.message || t('auth.serviceErrors.LOGOUT_FAILED');
    }
  }).catch((error) => {
    returnObject.success = false;
    const status = error?.response?.status;
    // Even if API call fails, consider it successful if we're logging out
    // The backend may have already cleared cookies and invalidated session
    if (status === 401 || status === 403) {
      // Token already invalid/expired - logout is effectively successful
      returnObject.success = true;
      returnObject.message = t('auth.logoutSuccess') || 'Logged out successfully';
    } else if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : (error?.response?.data?.message || t('auth.serviceErrors.LOGOUT_FAILED'));
    }
  })

  return returnObject;
}

export const signUpWithCookies = async (formData) => {

  const returnObject = { success: false, message: '', data: null, requiresVerification: false, retryAfter: 0 };

  await retryWithBackoff(
    () => apiClient.post('/auth/cookie/signup', formData)
  ).then((response) => {

    returnObject.data = response.data;
    // Also include code directly in returnObject if present
    if (response.data?.code) {
      returnObject.code = response.data.code;
    }

    // Check if email verification is required
    const responseCode = response.data?.code || response.data?.message;
    const responseStatus = response.data?.status || response.status;
    if (responseCode === 'AUTH_EMAIL_VERIFICATION_REQUIRED' || responseStatus === 201) {
      returnObject.success = true; // Still success, but requires verification
      returnObject.requiresVerification = true;
      returnObject.message = response.data?.message || t('auth.emailVerificationRequired') || 'Please verify your email to continue';
    } else if (responseCode === 'AUTH_SIGNUP_SUCCESS' || responseStatus === 200) {
      returnObject.success = true;
      returnObject.requiresVerification = false;
      returnObject.message = t('auth.signupSuccess') || response.data?.message || 'Account created successfully';
      // Extract and store token expiration if tokens are present
      const tokens = extractTokensFromResponse(response);
      if (tokens.accessToken && tokens.expiresIn && typeof tokens.expiresIn === 'number' && tokens.expiresIn > 0) {
        setTokenExpiration(tokens.expiresIn);
      }
    } else {
      // Default: treat as success if status is 200
      returnObject.success = true;
      returnObject.requiresVerification = true; // Default to requiring verification for safety
      // Extract and store token expiration if tokens are present
      const tokens = extractTokensFromResponse(response);
      if (tokens.accessToken && tokens.expiresIn && typeof tokens.expiresIn === 'number' && tokens.expiresIn > 0) {
        setTokenExpiration(tokens.expiresIn);
      }
    }

  }).catch((error) => {

    // Extract rate limit info
    const rateLimitInfo = extractRateLimitInfo(error);
    returnObject.retryAfter = rateLimitInfo.retryAfter;

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.SIGNUP_FAILED');
    }
  })

  return returnObject;
};

export const refreshToken = async () => {
  const returnObject = { success: true, message: '', data: null };
  const refreshToken = getRefreshToken(); // Uses active storage type tracking

  // Determine which storage type to use based on active storage type
  const activeStorageType = getActiveStorageType();
  const isUsingSessionStorage = activeStorageType === 'sessionStorage';

  if (!refreshToken) {
    returnObject.success = false;
    returnObject.message = t('auth.serviceErrors.REFRESH_FAILED') || 'No refresh token available';
    return returnObject;
  }

  await retryWithBackoff(
    () => apiClient.post('/auth/refresh-token', {
      refreshToken: refreshToken
    })
  ).then((response) => {
    returnObject.data = response.data;

    // Extract and store tokens from response
    const tokens = extractTokensFromResponse(response);

    // Store tokens in the same storage type as the original tokens
    if (tokens.accessToken) {
      if (isUsingSessionStorage) {
        setAccessTokenToSessionStorage(tokens.accessToken);
      } else {
        setAccessTokenToLocalStorage(tokens.accessToken);
      }
    }
    if (tokens.refreshToken) {
      if (isUsingSessionStorage) {
        setRefreshTokenToSessionStorage(tokens.refreshToken);
      } else {
        setRefreshTokenToLocalStorage(tokens.refreshToken);
      }
    }
    if (tokens.expiresIn) {
      setTokenExpiration(tokens.expiresIn);
    }

  }).catch((error) => {
    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.REFRESH_FAILED');
    }
  })

  return returnObject;
};

export const refreshCookies = async () => {

  const returnObject = { success: false, message: '', data: null };

  try {
    const response = await retryWithBackoff(
      () => apiClient.post('/auth/cookie/refresh')
    );

    // Check if response is successful (status 200-299)
    if (response.status >= 200 && response.status < 300) {
      returnObject.success = true;
      returnObject.data = response.data;

      // Note: For cookie-based auth, tokens are in httpOnly cookies set by the server
      // We cannot and should not try to set them from JavaScript.
      // We only extract the token expiration for client-side token expiry checks.

      const expiresIn = response.data?.data?.expiresIn || response.data?.expiresIn;

      if (expiresIn && typeof expiresIn === 'number') {
        setTokenExpiration(expiresIn);
      }
    } else {
      returnObject.success = false;
      returnObject.message = response.data?.message || t('auth.serviceErrors.REFRESH_FAILED');
    }
  } catch (error) {
    // Log refresh error for debugging
    console.error('[Auth] Cookie refresh error:', {
      error: error?.message,
      code: error?.response?.data?.code,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      url: error?.config?.url,
    });

    // If refresh token is missing, throw the error so interceptor can handle it properly
    if (error?.response?.data?.code === 'AUTH_REFRESH_TOKEN_REQUIRED') {
      throw error; // Re-throw to let interceptor handle logout
    }

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : (error?.response?.data?.message || t('auth.serviceErrors.REFRESH_FAILED'));
    }
  }

  return returnObject;
};

export const setUserPrefCookies = async () => {

  const returnObject = { success: true, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.post('/auth/cookie/set-preference')
  ).then((response) => {

    returnObject.data = response.data;

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.SET_PREFERENCE_FAILED');
    }
  })

  return returnObject;
};

export const securityEvent = async (limit) => {

  const returnObject = { success: true, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.get('/auth/cookie/security-events', { params: { limit } })
  ).then((response) => {

    returnObject.data = response.data;

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.SECURITY_EVENT_FAILED');
    }
  })

  return returnObject;
};

export const activeSession = async () => {

  const returnObject = { success: true, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.get('/auth/cookie/active-sessions')
  ).then((response) => {

    returnObject.data = response.data;

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.ACTIVE_SESSION_FAILED');
    }
  })

  return returnObject;
};

export const verifyEmail = async (formatDate) => {

  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.post('/auth/verify-email-code', formatDate)
  ).then((response) => {

    returnObject.success = true;
    returnObject.data = response.data;

    // Extract and store token expiration if tokens are present
    const tokens = extractTokensFromResponse(response);
    if (tokens.accessToken && tokens.expiresIn) {
      setTokenExpiration(tokens.expiresIn);
    }

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : (error?.response?.data?.message || t('auth.serviceErrors.ACTIVE_SESSION_FAILED'));
    }
  })

  return returnObject;
};

export const resendVerificationEmail = async (email) => {

  const returnObject = { success: true, message: '', data: null };

  // Build request body - include email if provided
  const requestBody = email ? { email } : {};

  await retryWithBackoff(
    () => apiClient.post('/auth/resend-verification-email', requestBody)
  ).then((response) => {

    returnObject.data = response.data;
    returnObject.message = response.data?.message || t('auth.verificationEmailResent') || 'Verification email sent successfully';

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.RESEND_VERIFICATION_FAILED') || 'Failed to resend verification email';
    }
  })

  return returnObject;
};

/**
 * Verify phone verification code (for SMS OTP)
 */
export const verifyPhoneCode = async (formData) => {
  const returnObject = { success: true, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.post('/auth/verify-phone-code', formData)
  ).then((response) => {
    returnObject.data = response.data;

    // Extract and store token expiration if tokens are present
    const tokens = extractTokensFromResponse(response);
    if (tokens.accessToken && tokens.expiresIn) {
      setTokenExpiration(tokens.expiresIn);
    }
  }).catch((error) => {
    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.VERIFY_PHONE_FAILED') || 'Failed to verify phone code';
    }
  })

  return returnObject;
};

/**
 * Get available OTP delivery methods (Email/SMS)
 */
export const getAvailableOtpMethods = async (email, phoneNumber) => {
  const returnObject = { success: false, message: '', data: null };

  const params = new URLSearchParams();
  if (email) params.append('email', email);
  if (phoneNumber) params.append('phoneNumber', phoneNumber);

  await retryWithBackoff(
    () => apiClient.get(`/auth/otp/available-methods?${params.toString()}`)
  ).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data?.data || response.data;
  }).catch((error) => {
    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.serviceErrors.GET_METHODS_FAILED') || 'Failed to get available methods';
    }
  })

  return returnObject;
};

/**
 * Send verification code after signup/login with chosen method (Email or SMS)
 */
export const sendVerificationCode = async (otpRequestToken, method) => {
  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.post('/auth/send-verification-code', {
      otpRequestToken,
      method
    })
  ).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data?.data || response.data;
    returnObject.message = response.data?.message || response.data?.data?.message ||
      (method === 'sms' ? t('auth.verificationCodeSentSMS') || 'Verification code sent via SMS' : t('auth.verificationEmailResent') || 'Verification code sent via email');
  }).catch((error) => {
    returnObject.success = false;
    const status = error?.response?.status;
    // Include error code and status from response
    returnObject.code = error?.response?.data?.code;
    returnObject.status = error?.response?.data?.status || status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) :
        (error?.response?.data?.message || t('auth.serviceErrors.SEND_VERIFICATION_FAILED') || 'Failed to send verification code');
      // Include available methods in error response if provided
      if (error?.response?.data?.data?.availableMethods) {
        returnObject.availableMethods = error.response.data.data.availableMethods;
      }
    }
  })

  return returnObject;
};

/**
 * Send password reset code with chosen method (Email or SMS)
 */
export const sendPasswordResetCode = async (otpRequestToken, method) => {
  const returnObject = { success: false, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.post('/auth/send-password-reset-code', {
      otpRequestToken,
      method
    })
  ).then((response) => {
    returnObject.success = true;
    returnObject.data = response.data?.data || response.data;
    returnObject.message = response.data?.message || response.data?.data?.message ||
      (method === 'sms' ? t('auth.resetCodeSentSMS') || 'Password reset code sent via SMS' : t('auth.resetCodeSentEmail') || 'Password reset code sent via email');
  }).catch((error) => {
    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) :
        (error?.response?.data?.message || t('auth.serviceErrors.SEND_RESET_CODE_FAILED') || 'Failed to send password reset code');
      // Include available methods in error response if provided
      if (error?.response?.data?.data?.availableMethods) {
        returnObject.availableMethods = error.response.data.data.availableMethods;
      }
    }
  })

  return returnObject;
};

export const deleteUserPublic = async (email, code) => {

  const returnObject = { success: true, message: '', data: null };

  await retryWithBackoff(
    () => apiClient.post('/users/public/delete', { email, code })
  ).then((response) => {

    returnObject.data = response.data;
    returnObject.message = response.data?.message || t('auth.deleteUserPublicSuccess') || 'User public deleted successfully';

  }).catch((error) => {

    returnObject.success = false;
    const status = error?.response?.status;
    if (status === 503) {
      returnObject.message = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
    } else {
      returnObject.message = error?.response?.data?.code ? t(`auth.errorCodification.${error?.response?.data?.code}`) : t('auth.errorCodification.AUTH_VERIFICATION_FAILED') || 'Failed to delete user public';
    }
  })

  return returnObject;
};
